// weather/shell.jsx — the Weather page: a full takeover, opened from the
// home card or the menu.
//
// Two columns on the iPad. Left is today: the station reading, any land
// warning, the forecaster's own words and the details people ask about
// (UV window, fire danger, sun). Right is what is coming: the next 24
// hours as a chart, then seven days. A phone gets one column, with the
// hours and days ahead of the details.
//
// No colour for rain yet (2026-10-22): bars are ink, darker with a
// higher chance. Night hours sit on a quiet band.
//
// Places: a switch at the top when HA has more than one BOM entry
// (model.js LOCATIONS). It always opens on Home and goes back to Home
// when the page closes (Ben, 2026-10-22); nothing else on the panel
// follows it.

import * as React from 'react';
import { TakeoverShell, Card, A_FONT, ALabel, fs, Segmented } from '../direction-a.jsx';
import { RADIUS } from '../theme.js';
import { Degrees } from '../degrees.jsx';
import { WeatherIcon } from './icons.jsx';
import { WarningLine, chanceStyle } from './card.jsx';
import {
  rainAmount, fmtHour, fmtClock, fmtDay, iconForDescriptor, localDate, EVENING_HOUR,
  availableLocations,
} from './model.js';
import { useWeather } from './use-weather.jsx';

function useWidth() {
  const ref = React.useRef(null);
  const [w, setW] = React.useState(0);
  React.useLayoutEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([e]) => setW(Math.floor(e.contentRect.width)));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

// ── Now ────────────────────────────────────────────────────────────

function NowCard({ T, current, icon, viewport }) {
  const big = viewport === 'phone' ? 64 : 76;
  const label = current.station
    ? `Now · ${current.station}${current.at ? `, ${fmtClock(current.at)}` : ''}`
    : 'Now';
  if (current.temp == null) {
    return (
      <Card T={T} eyebrow={label}>
        <div style={{ fontSize: fs(T, 15), color: T.inkSoft }}>
          No current reading from the station{current.at ? ` since ${fmtClock(current.at)}` : ''}.
        </div>
      </Card>
    );
  }
  const facts = [
    current.feels != null && ['Feels like', `${Math.round(current.feels)}°`],
    current.humidity != null && ['Humidity', `${current.humidity}%`],
    current.wind != null && ['Wind', `${current.windDir ? `${current.windDir} ` : ''}${current.wind} km/h${current.gust ? `, gusts ${current.gust}` : ''}`],
  ].filter(Boolean);
  return (
    <Card T={T} eyebrow={label}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {icon && <span style={{ color: T.ink }}><WeatherIcon name={icon} size={44} bg={T.card} strokeWidth={1.3}/></span>}
          <Degrees T={T} value={current.temp} size={big} weight={400}/>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 16, rowGap: 6, fontSize: fs(T, 15) }}>
          {facts.map(([k, v]) => (
            <React.Fragment key={k}>
              <span style={{ color: T.inkSoft }}>{k}</span>
              <span style={{ color: T.ink, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{v}</span>
            </React.Fragment>
          ))}
        </div>
      </div>
    </Card>
  );
}

// ── Warnings ───────────────────────────────────────────────────────

function untilText(until, now) {
  if (!until) return '';
  const day = localDate(until) === localDate(now) ? '' : `${until.toLocaleDateString('en-AU', { weekday: 'short' })} `;
  return `, until ${day}${fmtClock(until)}`;
}

function WarningsCard({ T, warnings, now }) {
  if (!warnings.length) return null;
  return (
    <Card T={T} style={{ gap: 12 }}>
      {warnings.map((w) => (
        <div key={w.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <WarningLine T={T} warnings={[w]}/>
          <div style={{ fontSize: fs(T, 14), color: T.ink, paddingLeft: 18 }}>
            {w.detail}{untilText(w.until, now)}
          </div>
        </div>
      ))}
    </Card>
  );
}

// ── Today, in the forecaster's words ───────────────────────────────

function Detail({ T, label, value, sub }) {
  if (value == null) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
      <span style={{ fontSize: fs(T, 13), color: T.inkSoft }}>{label}</span>
      <span style={{ fontSize: fs(T, 16), color: T.ink, fontWeight: 500 }}>{value}</span>
      {sub && <span style={{ fontSize: fs(T, 13), color: T.inkSoft }}>{sub}</span>}
    </div>
  );
}

function TodayCard({ T, days, current, now }) {
  const today = days[0];
  if (!today) return null;
  const tomorrow = days[1];
  const evening = now.getHours() >= EVENING_HOUR;
  const para = { fontSize: fs(T, 16), lineHeight: 1.5, color: T.ink, margin: 0 };
  const amount = rainAmount(today.rainMin, today.rainMax);
  const chance = today.rainChance != null ? `${today.rainChance}%${amount ? `, ${amount}` : ''}` : null;
  return (
    <Card T={T} eyebrow="Today, from the Bureau">
      {today.extended && <p style={para}>{today.extended}</p>}
      {evening && tomorrow?.extended && (
        <>
          <ALabel T={T} style={{ marginTop: 4 }}>Tomorrow</ALabel>
          <p style={para}>{tomorrow.extended}</p>
        </>
      )}
      <div style={{ height: 1, background: T.hair, margin: '4px 0' }}/>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 20, rowGap: 16 }}>
        <Detail T={T} label="Rain" value={chance}
          sub={current.rainSince9am != null ? `${current.rainSince9am} mm since 9 am` : null}/>
        <Detail T={T} label="UV" value={today.uvCategory ? `${today.uvCategory}${today.uvMax != null ? ` (${today.uvMax})` : ''}` : null}
          sub={today.uvStart && today.uvEnd ? `Sun protection ${fmtClock(today.uvStart)} to ${fmtClock(today.uvEnd)}` : null}/>
        <Detail T={T} label="Fire danger" value={today.fire}/>
        <Detail T={T} label="Sun" value={today.sunrise && today.sunset ? `${fmtClock(today.sunrise)} to ${fmtClock(today.sunset)}` : null}
          sub="Sunrise to sunset"/>
      </div>
    </Card>
  );
}

// ── Next 24 hours ──────────────────────────────────────────────────

function HoursChart({ T, hours, days }) {
  const [ref, width] = useWidth();
  const n = hours.length;
  const H = 214;
  const top = 40, tempH = 70;          // temperature band
  const barTop = 136, barH = 46;       // rain band
  const labelY = 206;
  const ok = width > 0 && n > 1;
  const step = ok ? width / n : 0;
  const temps = hours.map((h) => h.temp).filter((t) => t != null);
  const lo = Math.min(...temps), hi = Math.max(...temps);
  const span = Math.max(hi - lo, 4);
  const x = (i) => i * step + step / 2;
  const y = (t) => top + tempH - ((t - lo) / span) * tempH;
  // A labelled tick needs ~56 px or its temperature collides with the
  // next one; the phone gets every fourth hour, the iPad every third.
  const every = ok ? Math.max(1, Math.ceil(n / Math.max(1, Math.floor(width / 56)))) : 3;
  const ticks = hours.map((_, i) => i).filter((i) => i % every === 0);

  // Night bands from each day's sunset to the next sunrise.
  const night = hours.map((h) => h.icon === 'moon' || h.icon === 'partMoon' || (() => {
    const d = days.find((dd) => dd.date === localDate(h.t));
    return d?.sunrise && d?.sunset ? (h.t < d.sunrise || h.t >= d.sunset) : false;
  })());

  const line = hours.map((h, i) => (h.temp == null ? null : `${x(i).toFixed(1)},${y(h.temp).toFixed(1)}`)).filter(Boolean).join(' ');
  const sans = { fontFamily: A_FONT.sans };

  return (
    <div ref={ref} style={{ width: '100%' }}>
      {ok && (
        <svg width={width} height={H} role="img" aria-label="Temperature and chance of rain for the next 24 hours" style={{ display: 'block', overflow: 'visible' }}>
          {night.map((isN, i) => isN && (
            <rect key={`n${i}`} x={i * step} y={0} width={step + 0.5} height={labelY - 14} fill={T.cardSoft}/>
          ))}
          {ticks.map((i) => (
            <foreignObject key={`i${i}`} x={x(i) - 11} y={2} width={22} height={22}>
              <div style={{ color: T.ink }}><WeatherIcon name={hours[i].icon} size={22} bg={night[i] ? T.cardSoft : T.card}/></div>
            </foreignObject>
          ))}
          <polyline points={line} fill="none" stroke={T.ink} strokeWidth={1.75} strokeLinejoin="round" strokeLinecap="round"/>
          {ticks.map((i) => hours[i].temp != null && (
            <g key={`t${i}`}>
              <circle cx={x(i)} cy={y(hours[i].temp)} r={3} fill={T.ink}/>
              <text x={x(i)} y={y(hours[i].temp) - 9} textAnchor="middle" fontSize={fs(T, 14)} fontWeight={500} fill={T.ink} style={sans}>
                {Math.round(hours[i].temp)}°
              </text>
            </g>
          ))}
          <line x1={0} x2={width} y1={barTop + barH} y2={barTop + barH} stroke={T.hair}/>
          {hours.map((h, i) => {
            const c = h.chance || 0;
            if (c <= 0) return null;
            const bh = Math.max(2, (c / 100) * barH);
            return (
              <rect key={`b${i}`} x={i * step + step * 0.18} y={barTop + barH - bh} width={step * 0.64} height={bh}
                rx={1.5} fill={T.ink} opacity={0.14 + 0.62 * (c / 100)}/>
            );
          })}
          {ticks.map((i) => (hours[i].chance || 0) >= 20 && (
            <text key={`c${i}`} x={x(i)} y={barTop - 6} textAnchor="middle" fontSize={fs(T, 12)}
              fill={chanceStyle(T, hours[i].chance).color} fontWeight={chanceStyle(T, hours[i].chance).fontWeight} style={sans}>
              {hours[i].chance}%
            </text>
          ))}
          {ticks.map((i) => (
            <text key={`h${i}`} x={x(i)} y={labelY} textAnchor="middle" fontSize={fs(T, 13)} fill={T.inkSoft} style={sans}>
              {i === 0 ? 'Now' : fmtHour(hours[i].t)}
            </text>
          ))}
        </svg>
      )}
    </div>
  );
}

function HoursCard({ T, hours, days, pending }) {
  // Another place's hourly forecast arrives a moment after the switch;
  // hold the chart's height rather than flash "isn't available".
  if (pending) return <Card T={T} eyebrow="Next 24 hours"><div style={{ height: 214 }}/></Card>;
  if (!hours.length) {
    return <Card T={T} eyebrow="Next 24 hours"><div style={{ color: T.inkSoft, fontSize: fs(T, 15) }}>The hourly forecast isn't available.</div></Card>;
  }
  return (
    <Card T={T} eyebrow="Next 24 hours"
      headerAction={<span style={{ fontSize: fs(T, 13), color: T.inkSoft }}>Bars show the chance of rain</span>}>
      <HoursChart T={T} hours={hours} days={days}/>
    </Card>
  );
}

// ── Seven days ─────────────────────────────────────────────────────

function DaysCard({ T, days, now, viewport }) {
  const [open, setOpen] = React.useState(null);
  const lows = days.map((d) => d.min).filter((v) => v != null);
  const highs = days.map((d) => d.max).filter((v) => v != null);
  const lo = Math.min(...lows, ...highs), hi = Math.max(...lows, ...highs);
  const span = Math.max(hi - lo, 1);
  // The phone drops the range bar: at 390 px it squeezed the forecast
  // text to a single word.
  const phone = viewport === 'phone';
  const barW = 96;
  return (
    <Card T={T} eyebrow="Seven days" style={{ gap: 0 }}>
      {days.map((d, i) => {
        const isOpen = open === d.date;
        const amount = rainAmount(d.rainMin, d.rainMax) ? ` · ${rainAmount(d.rainMin, d.rainMax).replace(' to ', '–')}` : '';
        const a = d.min ?? d.max, b = d.max ?? d.min;
        return (
          <div key={d.date} style={{ borderTop: i === 0 ? 'none' : `1px solid ${T.hair}` }}>
            <button onClick={() => setOpen(isOpen ? null : d.date)} aria-expanded={isOpen}
              style={{
                display: 'grid', alignItems: 'center', width: '100%', minHeight: 60,
                gridTemplateColumns: phone ? '46px 28px minmax(0, 1fr) 30px 30px' : `60px 30px minmax(0, 1fr) 30px ${barW}px 30px`,
                columnGap: 12, padding: '10px 0', border: 'none', background: 'none',
                cursor: d.extended ? 'pointer' : 'default', textAlign: 'left', color: T.ink, fontFamily: A_FONT.sans,
              }}>
              <span style={{ fontSize: fs(T, 15), fontWeight: 600 }}>{fmtDay(d.date, now)}</span>
              <span style={{ color: T.ink }}><WeatherIcon name={iconForDescriptor(d.icon, false)} size={26} bg={T.card}/></span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: fs(T, 15), ...(phone ? {} : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }) }}>{d.short || '–'}</span>
                {d.rainChance != null && (
                  <span style={{ display: 'block', fontSize: fs(T, 13), marginTop: 2, ...chanceStyle(T, d.rainChance), color: d.rainChance < 20 ? T.mute : chanceStyle(T, d.rainChance).color }}>
                    {d.rainChance}% rain{amount}
                  </span>
                )}
              </span>
              <span style={{ fontSize: fs(T, 15), color: T.inkSoft, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {d.min != null ? `${Math.round(d.min)}°` : ''}
              </span>
              {!phone && <span style={{ position: 'relative', height: 6, borderRadius: RADIUS.full, background: T.cardSoft }}>
                {a != null && (
                  <span style={{
                    position: 'absolute', top: 0, bottom: 0, borderRadius: RADIUS.full, background: T.inkSoft,
                    left: `${((a - lo) / span) * 100}%`, width: `${Math.max(((b - a) / span) * 100, 4)}%`,
                  }}/>
                )}
              </span>}
              <span style={{ fontSize: fs(T, 15), fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {d.max != null ? `${Math.round(d.max)}°` : ''}
              </span>
            </button>
            {isOpen && d.extended && (
              <p style={{ margin: '0 0 14px', paddingLeft: phone ? 0 : 102, fontSize: fs(T, 15), lineHeight: 1.5, color: T.inkSoft }}>
                {d.extended}
              </p>
            )}
          </div>
        );
      })}
    </Card>
  );
}

// ── The page ───────────────────────────────────────────────────────

// Mounted only while open, so the chosen place resets on close.
export function WeatherShell({ open, ...props }) {
  if (!open) return null;
  return <WeatherPage {...props}/>;
}

function WeatherPage({ T, onClose, weather, viewport, now, hass }) {
  const places = availableLocations(hass?.states);
  const [placeId, setPlaceId] = React.useState('home');
  const place = places.find((p) => p.id === placeId) || places[0];
  // Home's model comes from app.jsx, already live; another place gets
  // its own instance only while it is the one showing.
  const away = useWeather(hass, now, place.id === 'home' ? null : place.src);
  const { current, days, hours, hoursPending, warnings, ready } = away || weather;
  const phone = viewport === 'phone';
  const issued = days[0]?.issued;

  const body = !ready ? (
    <Card T={T}>
      <div style={{ fontSize: fs(T, 16), fontWeight: 500 }}>Weather unavailable</div>
      <div style={{ fontSize: fs(T, 14), color: T.inkSoft }}>
        The Bureau of Meteorology forecast isn't reaching Home Assistant, so nothing is shown rather than an old forecast.
      </div>
    </Card>
  ) : phone ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <NowCard T={T} current={current} icon={hours[0]?.icon} viewport={viewport}/>
      <WarningsCard T={T} warnings={warnings} now={now}/>
      <HoursCard T={T} hours={hours} days={days} pending={hoursPending}/>
      <DaysCard T={T} days={days} now={now} viewport={viewport}/>
      <TodayCard T={T} days={days} current={current} now={now}/>
    </div>
  ) : (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.25fr)', gap: 20, alignItems: 'start' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <NowCard T={T} current={current} icon={hours[0]?.icon} viewport={viewport}/>
        <WarningsCard T={T} warnings={warnings} now={now}/>
        <TodayCard T={T} days={days} current={current} now={now}/>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <HoursCard T={T} hours={hours} days={days} pending={hoursPending}/>
        <DaysCard T={T} days={days} now={now} viewport={viewport}/>
      </div>
    </div>
  );

  return (
    <TakeoverShell T={T} viewport={viewport} eyebrow={place.place} title="Weather" onClose={onClose} bodyless>
      <div style={{
        padding: phone ? '8px 18px calc(32px + env(safe-area-inset-bottom))' : '12px 36px calc(40px + env(safe-area-inset-bottom))',
        maxWidth: 1180, margin: '0 auto', boxSizing: 'border-box',
      }}>
        {places.length > 1 && (
          <div style={{ display: 'flex', marginBottom: phone ? 16 : 20 }}>
            <Segmented T={T} ariaLabel="Place" height={phone ? 42 : 44} stretch={phone}
              options={places.map((p) => ({ value: p.id, label: p.label }))}
              value={place.id} onChange={setPlaceId}/>
          </div>
        )}
        <React.Fragment key={place.id}>{body}</React.Fragment>
        <div style={{ marginTop: 18, fontSize: fs(T, 12), color: T.mute, textAlign: 'center' }}>
          Bureau of Meteorology{issued ? `, forecast issued ${fmtClock(issued)}` : ''}
        </div>
      </div>
    </TakeoverShell>
  );
}
